// resolveClusterConfig.ts — Industry Engine Phase 1, the cluster-config resolver.
//
// PAIRED MODULE — byte-identical content in:
//   crewbit-mobile/data/resolveClusterConfig.ts
//   crewbit-admin/src/data/resolveClusterConfig.ts
// Per decision D-admin: the DB tables (cluster_config / sub_industry_override /
// tenant_override — migration 20260522131252) are the single source of truth;
// these two TS files are thin resolvers over that data. This file is PURE (zero
// imports), so the two copies are identical and cannot drift.
//
// The DB fetch of the reference rows is the consuming surface's job (wired in
// sub-phase 1b). Keeping this file pure makes resolveClusterConfig() directly
// unit-testable with no DB / React Native / DOM dependency.
//
// Cascade (later wins):
//   cluster_config[ sub_industry_override[categoryId].cluster_key ]   -- dense base
//     (+) sub_industry_override.{behavior,routing}_overrides           -- sparse
//     (+) tenant_override.{behavior,routing,display}_overrides         -- sparse
// Unknown / unmapped category -> cluster B. Never throws.
//
// Phase 2 note: the dialect axis (dialectRef / dialectLabelSet /
// showCrewLens / sub_industry_override.display_alias_overrides) was retired
// in migration 20260523001958. The Crew lens is now derived from team count
// (useHasCrew), not stored. tenant_override.behavior_overrides is written by
// set_tenant_business_shape (Phase 2 ships the hasVehicleEntity signal).

// ── Resolved output — complete, no optionals ───────────────────────────────

export interface ResolvedClusterConfig {
  clusterKey: string;
  defaultLandingZoom: string;
  defaultLandingLens: string;
  fabShape: string[];
  recurringBias: boolean;
  resourcePrimitive: string;
  hasPropertyEntity: boolean;
  hasVehicleEntity: boolean;
  showRouteLens: boolean;
  workDropdownSurfaces: string[];
}

// ── DB row shapes (mirror migrations 20260522131252 + 20260523001958) ──────

export interface ClusterConfigRow {
  cluster_key: string;
  default_landing_zoom: string;
  default_landing_lens: string;
  fab_shape: string[];
  recurring_bias: boolean;
  resource_primitive: string;
  has_property_entity: boolean;
  has_vehicle_entity: boolean;
  show_route_lens: boolean;
  work_dropdown_surfaces: string[];
}

// The two jsonb override columns are contracted as Partial<ResolvedClusterConfig>
// — a writer sets a subset of resolved fields (camelCase). Phase 1 left them
// null; Phase 2 retired display_alias_overrides (dialect-axis remnant). Phase
// 3 will populate behavior_overrides / routing_overrides per sub-industry.
export interface SubIndustryOverrideRow {
  category_key: string;
  cluster_key: string;
  behavior_overrides: Partial<ResolvedClusterConfig> | null;
  routing_overrides: Partial<ResolvedClusterConfig> | null;
}

export interface TenantOverrideRow {
  business_id: string;
  display_overrides: Partial<ResolvedClusterConfig> | null;
  behavior_overrides: Partial<ResolvedClusterConfig> | null;
  routing_overrides: Partial<ResolvedClusterConfig> | null;
}

// Unknown / unmapped category resolves to this cluster — preserves the legacy
// dashboardConfig.getClusterKey() 'B' fallback.
export const FALLBACK_CLUSTER_KEY = 'B';

// Last-resort config, used ONLY if cluster_config failed to load entirely (DB
// unreachable). The DB is the source of truth; this is a crash-guard so the
// resolver still returns a complete object and never throws. Jobs-oriented
// (cluster-B-shaped) so a degraded app stays usable.
const CRASH_GUARD: ResolvedClusterConfig = {
  clusterKey: FALLBACK_CLUSTER_KEY,
  defaultLandingZoom: 'day',
  defaultLandingLens: 'calendar',
  fabShape: ['snap_receipt'],
  recurringBias: false,
  resourcePrimitive: 'job',
  hasPropertyEntity: false,
  hasVehicleEntity: false,
  showRouteLens: false,
  workDropdownSurfaces: ['jobs'],
};

function clusterRowToResolved(row: ClusterConfigRow): ResolvedClusterConfig {
  return {
    clusterKey: row.cluster_key,
    defaultLandingZoom: row.default_landing_zoom,
    defaultLandingLens: row.default_landing_lens,
    fabShape: row.fab_shape,
    recurringBias: row.recurring_bias,
    resourcePrimitive: row.resource_primitive,
    hasPropertyEntity: row.has_property_entity,
    hasVehicleEntity: row.has_vehicle_entity,
    showRouteLens: row.show_route_lens,
    workDropdownSurfaces: row.work_dropdown_surfaces,
  };
}

export interface ResolveClusterConfigInput {
  /** The business's industry category id (INDUSTRY_CATEGORIES[].id). */
  categoryId: string | null | undefined;
  /** All cluster_config rows (7), loaded from the DB. */
  clusterConfigs: ClusterConfigRow[];
  /** All sub_industry_override rows (17), loaded from the DB. */
  subIndustryOverrides: SubIndustryOverrideRow[];
  /** The business's tenant_override row, or null when none exists. */
  tenantOverride: TenantOverrideRow | null;
}

/**
 * Resolve a business's complete cluster config.
 *
 * Pure — no DB access; the caller loads the reference rows. Returns a
 * ResolvedClusterConfig with no optionals. Unknown / unmapped category resolves
 * to cluster B. Never throws.
 */
export function resolveClusterConfig(
  input: ResolveClusterConfigInput,
): ResolvedClusterConfig {
  const { categoryId, clusterConfigs, subIndustryOverrides, tenantOverride } = input;

  // 1. category -> cluster_key via sub_industry_override; unknown -> B.
  const subRow =
    (categoryId
      ? subIndustryOverrides.find((s) => s.category_key === categoryId)
      : undefined) ?? null;
  const clusterKey = subRow?.cluster_key ?? FALLBACK_CLUSTER_KEY;

  // 2. dense base = cluster_config[clusterKey]; fall back to B, then crash-guard.
  const baseRow =
    clusterConfigs.find((c) => c.cluster_key === clusterKey) ??
    clusterConfigs.find((c) => c.cluster_key === FALLBACK_CLUSTER_KEY);
  const base: ResolvedClusterConfig = baseRow
    ? clusterRowToResolved(baseRow)
    : { ...CRASH_GUARD };

  // 3. cascade the sparse override layers, later wins. clusterKey is identity
  //    (set by the category->cluster mapping) — pinned last so no override
  //    layer shifts it. Phase 2 retired sub_industry_override.display_alias_
  //    overrides; tenant_override.display_overrides remains for future use.
  return {
    ...base,
    ...(subRow?.behavior_overrides ?? {}),
    ...(subRow?.routing_overrides ?? {}),
    ...(tenantOverride?.behavior_overrides ?? {}),
    ...(tenantOverride?.routing_overrides ?? {}),
    ...(tenantOverride?.display_overrides ?? {}),
    clusterKey: base.clusterKey,
  };
}
